/**
 * Utility functions to track time spent on lessons using localStorage
 */

// Key format for localStorage: lessonTime_{courseId}_{lessonId}
const getStorageKey = (courseId, lessonId) => `lessonTime_${courseId}_${lessonId}`;

/**
 * Get the time spent on a specific lesson
 * @param {string} courseId - The course ID
 * @param {string} lessonId - The lesson ID
 * @returns {number} - Time spent in seconds
 */
export const getLessonTime = (courseId, lessonId) => {
  if (!courseId || !lessonId) return 0;
  
  const key = getStorageKey(courseId, lessonId);
  const storedTime = localStorage.getItem(key);
  
  return storedTime ? parseInt(storedTime, 10) : 0;
};

/**
 * Update the time spent on a specific lesson
 * @param {string} courseId - The course ID
 * @param {string} lessonId - The lesson ID
 * @param {number} additionalSeconds - Additional seconds to add
 * @returns {number} - Updated total time in seconds
 */
export const updateLessonTime = (courseId, lessonId, additionalSeconds) => {
  if (!courseId || !lessonId) return 0;
  
  const key = getStorageKey(courseId, lessonId);
  const currentTime = getLessonTime(courseId, lessonId);
  const newTime = currentTime + additionalSeconds;
  
  localStorage.setItem(key, newTime.toString());
  return newTime;
};

/**
 * Get all lesson times for a course
 * @param {string} courseId - The course ID
 * @returns {Object} - Object with lesson IDs as keys and times as values
 */
export const getAllLessonTimes = (courseId) => {
  if (!courseId) return {};
  
  const result = {};
  const prefix = `lessonTime_${courseId}_`;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const lessonId = key.substring(prefix.length);
      result[lessonId] = parseInt(localStorage.getItem(key), 10);
    }
  }
  
  return result;
};

/**
 * Clear all lesson times for a course
 * @param {string} courseId - The course ID
 */
export const clearLessonTimes = (courseId) => {
  if (!courseId) return;
  
  const prefix = `lessonTime_${courseId}_`;
  
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      localStorage.removeItem(key);
    }
  }
}; 